import React, { FC } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { CurrencyCode } from '../../../../../api/services/site-configs/types';
import type { FreeBet } from 'app/api/services/freebets/types';
import type { ReduxState } from 'app/redux/types';

import { Trans } from 'app/lang';

import selectCurrency from '../../../../../redux/selectors/сurrency';
import { AmountValidationResult, SubmissionState } from '../../../constants';
import { RecordOfBetInfoType } from '../../../records';
import { selectHasEnoughMoney } from '../../../selectors';
import { selectAmountStakeType } from '../../../selectors/stakeTypes';
import {
  selectExpressAmountValidation,
  selectExpressFreeBet, selectExpressFreebetMaxWinning,
  selectExpressLimits,
  selectExpressMaxWinning,
  selectExpressPrices,
  SelectExpressPricesValue,
} from '../../../selectors/stakeTypes/express';
import { selectOnlyAcceptAmountExpressError } from '../../../selectors/stakeTypes/single';
import { selectSubmission } from '../../../selectors/submission';
import Error from '../../EntryComponents/Error';
import { getPresetsAndFreebetsBar } from '../../FooterBar';
import MinLimitError from '../../MinLimitError';
import NoMoneyLink from '../../NoMoneyLink';
import PriceDiff from '../../PriceDiff';
import Submission from '../../Submission';
import TextWithAmountBar from '../../TextWithAmountBar';
import AmountInput from './AmountInput';

import dictionary from './Express.dictionary.json';

import styles from './ExpressStakeType.module.scss';


// ---

type StoreProps = {
  prices: SelectExpressPricesValue;
  amountValue: string;
  maxWinning: number;
  freeBetMaxWinning: number;
  disabled: boolean;
  hasEnoughMoney: boolean;
  minLimit: number;
  minLimitError: boolean;
  freeBet: FreeBet | null;
  currency: CurrencyCode;
  onlyAcceptAmountExpressError?: RecordOfBetInfoType['error'] | null;
};

const Express: FC<StoreProps> = ({
  prices,
  amountValue,
  maxWinning,
  freeBetMaxWinning,
  disabled,
  hasEnoughMoney,
  minLimit,
  minLimitError,
  freeBet,
  currency,
  onlyAcceptAmountExpressError,
}) => {
  const _textBarClassName = classNames({
    [styles.disabledTextBar]: disabled,
  });

  const nextPrice = disabled ? prices.prevPrice : prices.nextPrice;
  const hasInputError = !hasEnoughMoney || minLimitError || Boolean(onlyAcceptAmountExpressError);

  const renderFooterContent = () => {
    if (Boolean(onlyAcceptAmountExpressError)) {
      return (
        <Error error={onlyAcceptAmountExpressError}
               currency={currency} />
      );
    }

    if (!hasEnoughMoney) {
      return (
        <NoMoneyLink />
      );
    }

    if (minLimitError) {
      return (
        <MinLimitError value={minLimit} />
      );
    }

    if (freeBet && freeBetMaxWinning) {
      return (
        <TextWithAmountBar value={freeBetMaxWinning}
                           label={dictionary.maxWinningLabel} />
      );
    }

    if (amountValue) {
      return (
        <TextWithAmountBar value={maxWinning}
                           data-test="stakeTypeExpress-maxWinning"
                           label={dictionary.maxWinningLabel}
                           className={_textBarClassName} />
      );
    }

    const PresetsAndFreebetsBar = getPresetsAndFreebetsBar('express');
    return (
      <PresetsAndFreebetsBar />
    );
  };

  return (
    <div className={styles.container}
         data-test="stakeTypeExpress-container">
      {disabled ? (
        <Submission className={styles.submission} />
      ) : (
        <>
          <div className={styles.price}
               data-test="stakeTypeExpress-price">
            <Trans data={dictionary.priceContainerText}
                   components={{
                     coefficient: (
                       <PriceDiff data-test="stakeTypeExpress-priceDiff"
                                  prev={prices.prevPrice}
                                  next={nextPrice}
                                  className={styles.priceValue} />
                     ),
                   }} />
          </div>

          <AmountInput hasError={hasInputError} />

          {renderFooterContent()}
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => {
  const validation = selectExpressAmountValidation(state);

  return {
    prices: selectExpressPrices(state),
    amountValue: selectAmountStakeType(state).value,
    maxWinning: selectExpressMaxWinning(state),
    freeBetMaxWinning: selectExpressFreebetMaxWinning(state),
    disabled: selectSubmission(state) !== SubmissionState.AWAITING,
    hasEnoughMoney: selectHasEnoughMoney(state),
    minLimit: selectExpressLimits(state).min,
    minLimitError: validation === AmountValidationResult.MIN_NOT_REACHED,
    freeBet: selectExpressFreeBet(state),
    currency: selectCurrency(state),
    onlyAcceptAmountExpressError: selectOnlyAcceptAmountExpressError(state),
  };
};

export default connect(mapStateToProps)(Express);
